import React, { useMemo, Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import * as Sentry from '@sentry/browser'
import { setAppLocales, allLocales } from '@fs/zion-locale'
import { AppLayout } from '@fs/zion-ui'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { BrowserRouter } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import AlertBannerContext from './context/AlertBannerContext'
import AccountLockedProvider from './context/AccountLockedProvider'
import ReviewRoutes from './components/review/ReviewRoutes'

import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import useAlertBanner from './hooks/useAlertBanner'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// initialize Sentry for the app
if (sentryDSN) {
  // pass Sentry overrides to mergeSentryConfig
  Sentry.init(
    mergeSentryConfig({
      dsn: sentryDSN,
      // help avoid FS quota issues
      sampleRate: 0.1,
      ignoreErrors: [/loading chunk/i],
    })
  )
}

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''
const noOp = () => {
  /* Empty callback to block the default browser prompt */
}

// add any app logic here, languages and translations are configured and available here

const FrontierWrapper = () => {
  const alertBannerProps = useAlertBanner()
  const isReviewPage = useMemo(() => window.location.pathname.indexOf('/review') >= 0, [])
  setAppLocales(allLocales)
  const children = isReviewPage ? (
    <ReviewRoutes />
  ) : (
    <Root analytics alertBannerProps={alertBannerProps}>
      <AppLayout fullWidth>
        <AlertBannerContext.Provider value={alertBannerProps}>
          <AccountLockedProvider>
            <App />
          </AccountLockedProvider>
        </AlertBannerContext.Provider>
      </AppLayout>
    </Root>
  )

  return (
    <BrowserRouter basename={base} getUserConfirmation={noOp}>
      {children}
    </BrowserRouter>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = () => {
  return (
    <React.StrictMode>
      <Suspense fallback={<NoticeLoading />}>
        <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
          <FrontierWrapper />
        </FrontierBaseProviders>
      </Suspense>
    </React.StrictMode>
  )
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
